import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import {
  getStoredUser,
  getGuestCart,
  removeFromGuestCart,
  updateGuestCartQuantity,
  updateCartQuantity,
} from '../utils/guestStore';
import { useToast } from './Toast';
import BackButton from './BackButton';
import './CartPage.css';

const CartPage = ({ onBackClick, onCheckoutClick, onCartUpdate }) => {
  const toast = useToast();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const loadGuestCart = useCallback(() => {
    setCart(getGuestCart());
    setLoading(false);
  }, []);

  const fetchCart = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=cart&userId=${userId}`);
      const data = await response.json();
      if (data.status === 'ok') {
        setCart(data.cart || []);
      }
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const userData = getStoredUser();
    setUser(userData);
    if (userData) {
      fetchCart(userData.id);
    } else {
      loadGuestCart();
    }
  }, [fetchCart, loadGuestCart]);

  useEffect(() => {
    const onGuestCartUpdate = () => {
      if (!getStoredUser()) {
        loadGuestCart();
      }
    };
    window.addEventListener('guest-cart-updated', onGuestCartUpdate);
    return () => window.removeEventListener('guest-cart-updated', onGuestCartUpdate);
  }, [loadGuestCart]);

  const handleRemoveFromCart = async (productId) => {
    if (!user) {
      setCart(removeFromGuestCart(productId));
      if (onCartUpdate) onCartUpdate();
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}?action=remove_from_cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          productId,
        }),
      });
      const data = await response.json();
      if (data.status === 'ok') {
        setCart((prev) => prev.filter((item) => item.product_id !== productId));
        if (onCartUpdate) onCartUpdate();
      }
    } catch (err) {
      console.error('Error removing from cart:', err);
    }
  };

  const handleQuantityChange = async (productId, nextQuantity) => {
    if (nextQuantity < 1) {
      await handleRemoveFromCart(productId);
      return;
    }

    if (!user) {
      setCart(updateGuestCartQuantity(productId, nextQuantity));
      if (onCartUpdate) onCartUpdate();
      return;
    }

    try {
      const result = await updateCartQuantity(productId, nextQuantity);
      if (result.ok) {
        setCart((prev) =>
          prev.map((item) =>
            Number(item.product_id) === Number(productId)
              ? { ...item, quantity: nextQuantity }
              : item
          )
        );
        if (onCartUpdate) onCartUpdate();
      } else {
        toast.error(result.message || 'Failed to update quantity');
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
      toast.error('Failed to update quantity');
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.warning('Your cart is empty');
      return;
    }
    if (!user) {
      toast.warning('Please login to place your order');
      return;
    }
    if (onCheckoutClick) {
      onCheckoutClick(cart, calculateTotal());
    }
  };

  if (loading) {
    return <div className="cart-page"><div className="loading">Loading cart...</div></div>;
  }

  return (
    <div className="cart-page">
      <BackButton onClick={onBackClick} />
      <div className="page-title-header">
        <h1>🛒 Shopping Cart</h1>
      </div>

      {cart.length === 0 ? (
        <div className="cart-empty-container">
          <div className="empty-message">
            <span className="empty-icon">🛒</span>
            <p>Your cart is currently empty.</p>
            <p className="sub-text">Start shopping by browsing our products!</p>
          </div>
        </div>
      ) : (
        <div className="cart-container">
          <div className="cart-items">
            <div className="cart-items-header">
              <span>Product</span>
              <span>Price</span>
              <span>Quantity</span>
              <span>Total</span>
              <span>Action</span>
            </div>
            {cart.map((item) => (
              <div key={item.product_id} className="cart-item">
                <div className="item-product">
                  <img src={item.image_data || item.image} alt={item.name} />
                  <span className="item-name">{item.name}</span>
                </div>
                <div className="item-price">Rs. {item.price}</div>
                <div className="item-quantity">
                  <div className="cart-qty-control">
                    <button
                      type="button"
                      className="cart-qty-btn"
                      onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                      aria-label="Decrease quantity"
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    <span className="cart-qty-value">{item.quantity}</span>
                    <button
                      type="button"
                      className="cart-qty-btn"
                      onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>
                <div className="item-total">
                  Rs. {(item.price * item.quantity).toFixed(2)}
                </div>
                <div className="item-action">
                  <button
                    className="remove-btn"
                    onClick={() => handleRemoveFromCart(item.product_id)}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>Rs. {calculateTotal()}</span>
            </div>
            <div className="summary-row">
              <span>Shipping:</span>
              <span>Free</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>Rs. {calculateTotal()}</span>
            </div>
            {!user && (
              <p className="cart-login-note">Login required to place your order.</p>
            )}
            <button
              className="checkout-btn"
              onClick={handleCheckout}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
